<template>
  <div class="page-content">
    <section>
      <div class="content-wrap" style="margin-bottom: 0;">
        <label-filter :filter="filter" :disabled="isLoading" @change="updateFilter" @search="handleSearch" />
      </div>
    </section>

    <section>
      <div class="content-wrap content-main">
        <div class="heading">
          <p class="pagination-info">
            กำลังแสดง <span class="current-range">{{ paginationInfo.start }}-{{ paginationInfo.end }}</span> จากทั้งหมด
            <span class="total-count">{{ paging.total }}</span> รายการ
          </p>
          <el-button :disabled="isLoading" type="sc-asset" icon="el-icon-plus" @click="createLabel">
            สร้าง Label
          </el-button>
        </div>

        <div v-if="paging.total > 0" class="content">
          <div class="table-wrapper">
            <!-- Loading Overlay -->
            <loading-spinner v-if="isLoading" />

            <table :class="{ 'loading': isLoading }" class="label-table">
              <thead>
                <tr>
                  <th class="col-name-th">
                    <div class="th-content">
                      <span>ชื่อ Label (ไทย)</span>
                    </div>
                  </th>
                  <th class="col-name-en">
                    <div class="th-content">
                      <span>ชื่อ Label (อังกฤษ)</span>
                    </div>
                  </th>
                  <th class="col-package-count">
                    <div class="th-content">
                      <span>Package ที่เลือก</span>
                    </div>
                  </th>
                  <th class="col-actions">
                    <div class="th-content">
                      <span>จัดการ</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <label-table-row
                  v-for="(item, index) in items"
                  :key="getLabelKey(item, index)"
                  :label="item"
                  :disabled="isLoading"
                  @edit="editLabel"
                  @delete="confirmDelete"
                />
              </tbody>
            </table>
          </div>

          <div class="paging">
            <el-pagination
:background="true" :layout="'prev, pager, next'" :current-page="paging.page"
              :page-size="paging.limit" :total="paging.total" :disabled="isLoading" @current-change="handlePageChange" />
          </div>
        </div>

        <div v-if="paging.total === 0" class="empty">
          <!-- Loading Overlay for empty state during search -->
          <loading-spinner v-if="isLoading" />

          <img v-else src="@/assets/images/admin/empty.png" />
          <p v-if="!isLoading">
            <b class="unknown">{{ error || 'ไม่พบข้อมูล' }}</b>
          </p>
          <el-button
v-if="!error && !isLoading" :disabled="isLoading" type="sc-asset" icon="el-icon-plus"
            @click="createLabel">
            สร้าง Label
          </el-button>
        </div>
      </div>
    </section>

    <!-- Delete Confirmation Alert -->
    <alert-dialog
:visible="confirm.open" :title="confirm.title" :message="confirm.message" confirm-text="ลบ"
      confirm-type="danger" @confirm="confirmDeleteAction" @cancel="closeConfirm" />
  </div>
</template>

<script lang="js">

// Components
import LoadingSpinner from '@/components/general/LoadingSpinner'
import { backendAdminClient } from "@/dependencies";
import LabelFilter from '../components/LabelFilter'
import LabelTableRow from '../components/LabelTableRow'
import AlertDialog from '../components/AlertDialog'

// Services
import { LABEL_CACHE_KEY } from '../utils/labelValidation'

export default {
  components: {
    LabelFilter,
    LabelTableRow,
    LoadingSpinner,
    AlertDialog
  },
  data() {
    return {
      isLoading: false,
      paging: {
        page: 1,
        limit: 10,
        total: 0
      },
      items: [],
      filter: {
        text: ''
      },
      error: null,
      confirm: {
        open: false,
        title: 'ลบข้อมูล',
        message: '',
        label: null
      },
    }
  },
  computed: {
    paginationInfo() {
      const start = (this.paging.page - 1) * this.paging.limit + 1
      const end = Math.min(this.paging.page * this.paging.limit, this.paging.total)
      return { start, end }
    }
  },
  watch: {
    'paging.page': function watchPagingPage(curr, prev) {
      if (curr !== prev) {
        this.fetchLabels()
      }
    }
  },
  mounted() {
    this.$emit('sub-title', 'รายการ Label')
    this.fetchLabels()
  },
  methods: {
    createLabel() {
      this.$router.push({ name: 'LabelCreate' })
    },
    editLabel(label) {
      sessionStorage.setItem(LABEL_CACHE_KEY, JSON.stringify(label))
      this.$router.push({
        name: 'LabelEdit',
        params: { id: label.id }
      })
    },
    updateFilter(value) {
      this.filter.text = value.text
    },
    getLabelKey(item, index) {
      return `${item.id || "no-id"}-${item.nameTh || "no-name"}-${index}`
    },
    handleSearch() {
      this.paging.page = 1
      this.fetchLabels()
    },
    handlePageChange(page) {
      this.paging.page = page
    },
    async fetchLabels() {
      this.isLoading = true
      this.error = null

      try {
        const name = this.filter.text ? this.filter.text.trim() : null
        const { limit, page } = this.paging

        const params = { name, page, limit }
        const response = await backendAdminClient.subscriptions.label.list(params)

        // response is already a LabelResponse instance
        this.items = response.items || []

        // Update pagination
        Object.assign(this.paging, {
          page: response.page || page,
          total: response.total || 0
        })
      }
      catch (error) {
        this.error = error.message || 'Failed to load labels'
        this.items = []
        Object.assign(this.paging, { page: 1, total: 0 })

        this.$message.error(this.error)
      }
      finally {
        setTimeout(() => {
          this.isLoading = false
        }, 400)
      }
    },
    confirmDelete(label) {
      this.confirm.message = `ต้องการลบ Label "${label.nameTh}"?`
      this.confirm.label = label
      this.confirm.open = true
    },
    closeConfirm() {
      this.confirm.open = false
      this.confirm.label = null
    },
    async confirmDeleteAction() {
      if (!this.confirm.label) return

      try {
        this.isLoading = true
        await backendAdminClient.subscriptions.label.delete(this.confirm.label.id)

        this.$message.success('ลบ Label สำเร็จ')
        await this.fetchLabels()
      } catch (error) {
        this.$message.error('ลบ Label ไม่สำเร็จ')
      } finally {
        this.isLoading = false
        this.closeConfirm()
      }
    }
  }
}
</script>

<style lang="css" scoped>
/* Content Wrap - Override global styles */
.content-wrap {
  background-color: white;
  padding: var(--size-lg) 0 !important;
  border: 1px solid #e5e7eb;
  border-radius: var(--size-sm);
}

/* Add padding to search section and create button section */
section:first-child .content-wrap,
.content-main .heading {
  padding-left: var(--size-lg) !important;
  padding-right: var(--size-lg) !important;
}

.content-main {
  display: flex;
  flex-direction: column;
  gap: var(--size-md);
}

/* Add padding back for non-table sections */
.content-main> :not(.content) {
  padding: 0 var(--size-lg) !important;
}

/* Remove padding only for table section */
.content-main .content {
  margin: 0 calc(-1 * var(--size-lg)) !important;
  padding: 0 var(--size-lg) !important;
}

/* Filter Section */
.filter {
  display: flex;
  gap: var(--size-sm);
}

.filter.input-text {
  flex: 1;
  display: flex;
  gap: 0.75rem;
}

.filter.input-text .el-input__inner {
  flex: 1;
}

/* Heading */
.heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--size-lg);
  padding: 0;
}

.pagination-info {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  flex: 1;
  min-width: 0;
}

.pagination-info .current-range {
  font-weight: 600;
  color: #111827;
}

.pagination-info .total-count {
  font-weight: 600;
  color: #111827;
}

.heading .el-button {
  flex-shrink: 0;
  min-width: fit-content;
}

/* Search button styling */
.filter .el-button {
  border: 1px solid #d1d5db;
  background: transparent;
  color: #6b7280;
  transition: all 0.3s ease;
}

.filter .el-button:hover {
  background-color: rgba(107, 114, 128, 0.1);
  border-color: #9ca3af;
  color: #374151;
}

.content {
  display: flex;
  flex-direction: column;
  gap: var(--size-md);
}

.table-wrapper {
  overflow-x: auto;
  position: relative;
}

.label-table.loading {
  opacity: 0.5;
  pointer-events: none;
}

/* Table */
.label-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-top: 1px solid #e5e7eb;
}

.label-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}

.label-table th .th-content {
  display: flex;
  align-items: center;
  padding: 16px 16px;
  font-size: 14px;
  font-weight: 600;
}

/* Column widths */
.col-name-th {
  width: 30%;
  min-width: 150px;
}

.col-name-en {
  width: 30%;
  min-width: 150px;
}

.col-package-count {
  width: 15%;
  min-width: 100px;
  text-align: center;
}

.col-package-count .th-content {
  justify-content: center;
}

.col-actions {
  width: 25%;
  min-width: 180px;
  text-align: center;
}

.col-actions .th-content {
  justify-content: center;
}

/* Table width for responsive design */
.label-table {
  min-width: 800px;
}

.label-table tbody tr:hover {
  background-color: #f9fafb;
}

/* Pagination */
.paging {
  display: flex;
  justify-content: center;
}

/* Empty State */
.empty {
  display: flex;
  flex-direction: column;
  gap: var(--size-xs);
  align-items: center;
  justify-self: center;
  padding: 40px 0;
  position: relative;
  min-height: 300px;
}

.empty img {
  width: 40%;
  min-width: 180px;
  aspect-ratio: 1/1;
}

.empty .unknown {
  color: rgb(144, 161, 185);
}

/* Responsive */
@media screen and (max-width: 1280px) {
  .empty img {
    width: 50%;
  }

  .col-name-th,
  .col-name-en {
    min-width: 120px;
  }

  .col-package-count {
    min-width: 80px;
  }

  .col-actions {
    min-width: 140px;
  }

  .label-table {
    min-width: 700px;
  }
}

@media screen and (max-width: 1024px) {
  .content-wrap {
    width: 85%;
    padding: var(--size-md);
  }

  .heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--size-lg);
    padding: var(--size-md);
  }

  .empty img {
    width: 60%;
  }

  .label-table th,
  .label-table td {
    padding: 12px 8px;
    font-size: 13px;
  }

  .col-name-th,
  .col-name-en {
    min-width: 100px;
  }

  .col-package-count {
    min-width: 70px;
  }

  .col-actions {
    min-width: 120px;
  }

  .label-table {
    min-width: 600px;
  }
}

@media screen and (max-width: 768px) {
  .content-wrap {
    width: 95%;
    padding: var(--size-sm);
  }

  .filter.input-text {
    flex-direction: column;
    gap: 0.5rem;
  }

  .filter.input-text .el-input__inner {
    width: 100%;
  }

  .filter.input-text .el-button {
    width: 100%;
  }

  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .label-table {
    min-width: 550px;
  }

  .label-table th,
  .label-table td {
    padding: 10px 6px;
    font-size: 12px;
  }

  .col-name-th,
  .col-name-en {
    min-width: 90px;
  }

  .col-package-count {
    min-width: 60px;
  }

  .col-actions {
    min-width: 100px;
  }
}

@media screen and (max-width: 640px) {
  .content-wrap {
    width: calc(100% - 1rem);
    padding: var(--size-sm);
  }

  .empty img {
    width: 80%;
  }

  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .label-table {
    min-width: 480px;
  }

  .label-table th,
  .label-table td {
    padding: 8px 4px;
    font-size: 11px;
  }

  .col-name-th,
  .col-name-en {
    min-width: 80px;
  }

  .col-package-count {
    min-width: 50px;
  }

  .col-actions {
    min-width: 85px;
  }
}

@media screen and (max-width: 480px) {
  .content-wrap {
    width: calc(100% - 0.5rem);
    padding: var(--size-xs);
  }

  .empty img {
    width: 100%;
  }

  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .label-table {
    min-width: 420px;
  }

  .label-table th,
  .label-table td {
    padding: 6px 3px;
    font-size: 10px;
  }

  .col-name-th,
  .col-name-en {
    min-width: 70px;
  }

  .col-package-count {
    min-width: 45px;
  }

  .col-actions {
    min-width: 90px;
  }
}
</style>